import React, { useState, useEffect, useId } from 'react';
import { X, Check, Zap, Coins, LogIn } from 'lucide-react';
import { UserProfile, UserSubscription } from '../types';
import { PayWithQr } from './PayWithQr';
import { CREDIT_PACKAGES, PRO_MONTHLY_USDC, PRO_LIST_USDC, PRO_DISCOUNT_PERCENT, formatUsdc } from '../utils/pricing';

interface PaymentModalProps {
  isOpen: boolean;
  onClose: () => void;
  user?: UserProfile | null;
  subscription?: UserSubscription;
  onSubscriptionUpdate?: () => void;
  /** Someone not signed in pressed Pay: open the sign-in */
  onSignIn?: () => void;
}

// Paying for Pro: choose how many months, then pay the exact sum of USDC to the site's
// address from any wallet (a Solana Pay QR, or the address copied). No wallet is connected
// to the site; the server credits the account once it sees the transfer (components/PayWithQr.tsx).

type ModalStep = 'choose' | 'pay' | 'success';

export const PaymentModal: React.FC<PaymentModalProps> = ({
  isOpen,
  onClose,
  user,
  subscription,
  onSubscriptionUpdate,
  onSignIn,
}) => {
  const [step, setStep] = useState<ModalStep>('choose');
  const [creditAmount, setCreditAmount] = useState<number>(CREDIT_PACKAGES[0].credits);
  const [paidCredits, setPaidCredits] = useState(0);
  const titleId = useId();

  // Each opening starts at the choice
  useEffect(() => {
    if (isOpen) setStep('choose');
  }, [isOpen]);

  if (!isOpen) return null;

  const totalCost = creditAmount * PRO_MONTHLY_USDC;
  const currentCredits = subscription?.credits || 0;

  return (
    <div className="ns-modal-scrim">
      <div role="dialog" aria-modal="true" aria-labelledby={titleId} className="ns-modal-sheet ns-modal-sheet-flush max-w-lg">
        {/* Header */}
        <div className="flex items-start justify-between gap-3 pl-6 pr-5 pt-5 pb-4 border-b border-cream-300">
          <div className="flex items-center gap-3 min-w-0">
            <span className="ns-mark ns-mark-lavender">
              <Coins />
            </span>
            <div className="min-w-0">
              <h2 id={titleId} className="ns-t-dialog">
                {step === 'choose' && (user ? 'Get Pro' : 'Pro')}
                {step === 'pay' && `Pay ${formatUsdc(totalCost)}`}
                {step === 'success' && 'Welcome to Pro'}
              </h2>
              <p className="text-sm text-ink-soft">
                {step === 'choose' && 'USDC on Solana, from any wallet'}
                {step === 'pay' && `${creditAmount} month${creditAmount === 1 ? '' : 's'} of Pro`}
                {step === 'success' && `${paidCredits} credit${paidCredits === 1 ? '' : 's'} on your account`}
              </p>
            </div>
          </div>
          <button onClick={onClose} className="ns-icon-btn" aria-label="Close">
            <X />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {step === 'choose' && (
            <>
              <div className="ns-well mb-6 p-4">
                <h3 className="ns-t-heading mb-2 flex items-center gap-2">
                  <Zap size={16} className="text-lavender-deep" aria-hidden="true" /> What Pro gives you
                </h3>
                <ul className="text-sm text-ink-soft space-y-1">
                  <li className="flex items-center gap-2"><Check size={14} className="text-ok" aria-hidden="true" /> Unlimited nodes</li>
                  <li className="flex items-center gap-2"><Check size={14} className="text-ok" aria-hidden="true" /> Cloud workspace sync</li>
                  <li className="flex items-center gap-2"><Check size={14} className="text-ok" aria-hidden="true" /> Unlimited workspaces</li>
                  <li className="flex items-center gap-2"><Check size={14} className="text-ok" aria-hidden="true" /> Cross-device access</li>
                </ul>
                <div className="mt-3 pt-3 border-t border-cream-300">
                  <p className="text-sm font-semibold text-ink">1 credit = {PRO_MONTHLY_USDC} USDC = 1 month of Pro <span className="text-ink-faint font-medium line-through">{PRO_LIST_USDC} USDC</span> <span className="ns-badge ns-badge-ok">{PRO_DISCOUNT_PERCENT}% off</span></p>
                </div>
              </div>

              <div className="mb-6">
                <p className="ns-field-label">How many months</p>
                <div className="ns-seg grid grid-cols-4 w-full rounded-2xl">
                  {CREDIT_PACKAGES.map((pkg) => (
                    <button
                      key={pkg.credits}
                      type="button"
                      onClick={() => setCreditAmount(pkg.credits)}
                      aria-pressed={creditAmount === pkg.credits}
                      className="ns-seg-item flex-col gap-0 rounded-xl py-2"
                    >
                      <span className="text-base">{pkg.credits}</span>
                      <span className="text-2xs font-medium text-ink-faint">{pkg.label}</span>
                    </button>
                  ))}
                </div>
                <p className="mt-3 text-center text-lg font-semibold text-ink">{formatUsdc(totalCost)}</p>
              </div>

              {user && currentCredits > 0 && (
                <div className="ns-note mb-4" data-tone="lavender">
                  <p>Credits now: <strong className="font-semibold">{currentCredits}</strong></p>
                </div>
              )}

              {user ? (
                <button type="button" onClick={() => setStep('pay')} className="ns-btn ns-btn-primary w-full">
                  <Coins size={16} aria-hidden="true" /> Pay {formatUsdc(totalCost)}
                </button>
              ) : (
                <>
                  <button type="button" onClick={() => { onClose(); onSignIn?.(); }} className="ns-btn ns-btn-primary w-full">
                    <LogIn size={16} aria-hidden="true" /> Sign in to pay
                  </button>
                  <p className="ns-t-meta mt-3 text-center">Pro is added to your account, so sign in or create one first. It takes a moment.</p>
                </>
              )}
            </>
          )}

          {step === 'pay' && (
            <>
              <PayWithQr
                credits={creditAmount}
                compact
                onPaid={({ newCredits }) => {
                  setPaidCredits(newCredits);
                  setStep('success');
                  onSubscriptionUpdate?.();
                }}
              />
              <button type="button" onClick={() => setStep('choose')} className="ns-btn ns-btn-quiet mt-4 w-full">Choose a different amount</button>
            </>
          )}

          {step === 'success' && (
            <div className="ns-empty px-0">
              <span data-tone="ok" className="ns-mark ns-mark-lg">
                <Check />
              </span>
              <h3 className="ns-empty-title">You are all set</h3>
              <p className="ns-empty-text">
                Pro is on your account: unlimited nodes, cloud sync, every workspace you want.
              </p>
              <button type="button" onClick={onClose} className="ns-btn ns-btn-primary w-full mt-4">
                Start making
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// The plan helpers live in utils/subscriptionAccess.ts; re-exported for the places that read them from here
export { getEffectiveTier, isSubscriptionValid, useFeatureAccess } from '../utils/subscriptionAccess';
